import { Button, Col, Form, FormInstance, Input, Row, Table } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { useEffect } from 'react';
import { Trash } from 'react-feather';
import moment from 'moment';
import { FloatingFormItem } from '@/components/_common';
import {
  OptionsAnswer,
  OptionsAnswerForm,
  QuestionFormValues,
} from '@/core/domain/crm/Questions/Questions.types';
import AntdFeatherIcon from '@/components/_common/AntdFeatherIcon';
import { DeleteButton } from '@/components/_common/Buttons';
import { COLUMNS_WIDTH, getTableScrollX } from '@/utils/table';

interface Props {
  optionsAnswer?: Array<OptionsAnswer & { key: string }>;
  form: FormInstance<QuestionFormValues>;
}

const MultipleChoices: React.FC<Props> = ({ optionsAnswer = [], form }) => {
  useEffect(() => {
    return () => form.resetFields(['optionsAnswer']);
  }, [form]);

  const addOption = () => {
    const curOptionsAnswer = (form.getFieldValue('optionsAnswer') ??
      []) as OptionsAnswer[];
    const optionsLength = curOptionsAnswer.length;
    const hasOptions = !!optionsLength;
    const defaultOption = {
      id: 0,
      description: '',
      answerOptionSequence: optionsLength + 1,
      key: String(moment().unix()),
    };

    if (hasOptions) {
      form.setFieldsValue({
        optionsAnswer: curOptionsAnswer.concat(defaultOption),
      });
    } else {
      form.setFieldsValue({
        optionsAnswer: [defaultOption],
      });
    }
  };

  const removeOption = (key: keyof OptionsAnswerForm, value: unknown) => {
    const newOptionsAnswer = optionsAnswer.filter(
      (item) => item[key] !== value,
    );

    form.setFieldsValue({ optionsAnswer: newOptionsAnswer });
  };

  const hasId = (id: number) => !!id && id !== 0;

  const columns: ColumnsType<OptionsAnswer> = [
    {
      title: 'Sequência',
      dataIndex: 'answerOptionSequence',
      ellipsis: true,
      width: 200,
      render: (sequence: number, row, i) => (
        <>
          {hasId(row.id) && (
            <Form.Item
              hidden
              name={['optionsAnswer', i, 'id']}
              initialValue={row.id}
            >
              <Input />
            </Form.Item>
          )}
          <FloatingFormItem
            style={{ marginBottom: 0 }}
            name={['optionsAnswer', i, 'answerOptionSequence']}
            label="Sequência"
            initialValue={sequence}
          >
            <Input />
          </FloatingFormItem>
        </>
      ),
    },
    {
      title: 'Descrição',
      dataIndex: 'description',
      ellipsis: true,
      width: 200,
      render: (description, _, i) => (
        <FloatingFormItem
          style={{ marginBottom: 0 }}
          name={['optionsAnswer', i, 'description']}
          label="Descrição"
          initialValue={description}
        >
          <Input />
        </FloatingFormItem>
      ),
    },
    {
      title: 'Ação',
      width: COLUMNS_WIDTH.action1,
      align: 'center',
      fixed: 'right',
      render: (row) => (
        <DeleteButton
          size="large"
          danger
          icon={<AntdFeatherIcon icon={<Trash />} />}
          type="ghost"
          onClick={() =>
            hasId(row.id)
              ? removeOption('id', row.id)
              : removeOption('key', row.key)
          }
        />
      ),
    },
  ];

  return (
    <>
      <Row
        justify="space-between"
        align="middle"
        style={{ marginBottom: 24, marginTop: 12 }}
      >
        <Col>
          <big>
            <strong>Opções de respostas</strong>
          </big>
        </Col>
        <Col>
          <Button type="primary" onClick={addOption}>
            Nova opção
          </Button>
        </Col>
      </Row>

      <Table
        className="table-small"
        dataSource={optionsAnswer}
        columns={columns}
        scroll={{ x: getTableScrollX(columns) }}
        pagination={false}
      />
    </>
  );
};

export default MultipleChoices;
